import {
	useLoaderData,
	useLocation,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { subtractArrays } from '#app/utils/array-utils'
import { cn } from '#app/utils/misc'
import { sortClerkFacetBySize, sortClerkFacets } from '#app/utils/search/sizes'
import { CheckboxSizeFilter } from '../size-dropdown'

export function SizeSelection() {
	let { t } = useTranslation('product_list_page')
	const data = useLoaderData<typeof loader>()
	const location = useLocation()
	const navigate = useNavigate()
	const navigation = useNavigation()
	// Define the styles for the expanded state
	const sizeFacets = (data.facetsData?.facets?.sizes as ClerkFacet[]).filter(
		facet => facet.value !== '',
	)
	const isNumberSize = (size: string) => /^\d+$/.test(size)

	const isKids = location.pathname.includes('kids') ?? false
	const kidsFormattedSizes = sizeFacets.sort(sortClerkFacetBySize)

	const internationalSizes = sizeFacets
		.filter((facet: ClerkFacet) => !isNumberSize(facet.value))
		.sort(sortClerkFacets)
	const euSizes = sizeFacets
		.filter((facet: ClerkFacet) => isNumberSize(facet.value))
		.sort(sortClerkFacets)

	const handleCheckboxChange = (
		checked: boolean,
		term: string[],
		key: string,
	) => {
		const params = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)

		if (checked) {
			term.forEach(value => params.append(key, value))
		} else {
			const values = params.getAll(key)
			const newValues = subtractArrays(values, term)
			params.delete(key)
			newValues.forEach(value => {
				params.append(key, value)
			})
		}
		// remove the pagination
		params.delete('p')
		navigate({
			pathname: '.',
			search: params.toString(),
		})
	}

	return (
		<div className="flex flex-col space-y-2 ">
			{/* International Sizes */}
			{!isKids && internationalSizes.length > 0 && (
				<div className="space-y-2">
					<PlentyBody size="md" className="text-green-40">
						{'International'}
					</PlentyBody>
					{internationalSizes.map(facet => {
						return (
							<CheckboxSizeFilter
								facet={facet}
								key={facet.name}
								handleCheckboxChange={handleCheckboxChange}
							/>
						)
					})}
				</div>
			)}
			{/* EU Sizes */}
			{!isKids && euSizes.length > 0 && (
				<div className="space-y-2">
					<PlentyBody
						size="md"
						className={cn(
							'text-green-40',
							euSizes.length > 0 ? 'block' : 'hidden',
						)}
					>
						{t('eu_sizes')}
					</PlentyBody>
					{euSizes.map(facet => {
						return (
							<CheckboxSizeFilter
								facet={facet}
								key={facet.name}
								handleCheckboxChange={handleCheckboxChange}
							/>
						)
					})}
				</div>
			)}
			{/* Kids Sizes */}
			{isKids && (
				<div className="space-y-2">
					{kidsFormattedSizes.map(facet => {
						return (
							<CheckboxSizeFilter
								facet={facet}
								key={facet.name}
								handleCheckboxChange={handleCheckboxChange}
							/>
						)
					})}
				</div>
			)}
		</div>
	)
}
